import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { Apis } from '../../core/apis';
import { KeywordsAndConstants } from '../../core/keywords-and-constants';

const API_THEME_URL = Apis.getUserLogoUrlAndColorCodeForTheme;

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private logoUrlSubject = new BehaviorSubject<string>(''); // Default logo
  public logoUrl$ = this.logoUrlSubject.asObservable();
  private iconUrlSubject = new BehaviorSubject<string>(''); // Default logo
  public iconUrl$ = this.iconUrlSubject.asObservable();
  private blackIconUrlSubject = new BehaviorSubject<string>(''); // Default logo
  public blackIconUrl$ = this.blackIconUrlSubject.asObservable();
  currentOrigin: any;
  constructor(
    rendererFactory: RendererFactory2,
    @Inject(PLATFORM_ID) private platformId: Object,
    public constants: KeywordsAndConstants,
    private http: HttpClient
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    if (isPlatformBrowser(this.platformId)) {
      this.currentOrigin = window.location.origin;
      // console.log(window.location.origin);
    }
  }

  getUserLogoUrlAndColorCode(): Observable<any> {
    let themeColorsString: any = localStorage.getItem('themeColors');
    if (themeColorsString && JSON.parse(themeColorsString) != null) {
      const themeColorsJson = JSON.parse(themeColorsString);
      this.setTheme(themeColorsJson);
    }
    let baseURL = `${API_THEME_URL}`;
    return this.http.get<any>(baseURL).pipe(
      tap((data) => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('themeColors', JSON.stringify(data));
          this.setTheme(data);
        }
      }),
      catchError((error) => {
        console.error('Failed to fetch theme data', error);
        this.setTheme('');
        return of('error', error);
      })
    );
  }
  dynamicManifest = {
    name: 'RemitAll',
    short_name: 'RemitAll',
    theme_color: '#6e90f6',
    background_color: '#fafafa',
    display: 'standalone',
    start_url: 'http://localhost:4200/',
    description:
      'My short project description giving a slightly better idea about my app',
    icons: [
      {
        src: 'http://localhost:4200/assets/pwa/manifest-icon-192.maskable.png',
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: 'http://localhost:4200/assets/pwa/manifest-icon-192.maskable.png',
        sizes: '192x192',
        type: 'image/png',
        purpose: 'maskable',
      },
      {
        src: 'http://localhost:4200/assets/pwa/manifest-icon-512.maskable.png',
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: 'http://localhost:4200/assets/pwa/manifest-icon-512.maskable.png',
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
      },
    ],
  };

  setTheme(data: any) {
    this.logoUrlSubject.next(
      data?.logoUrl || '/images/remitall-text-white.png'
    );
    this.iconUrlSubject.next(
      data?.iconUrl || '/images/remitall-icon-white.png'
    );
    this.blackIconUrlSubject.next(data?.iconUrl || '/images/logo.png');
    this.setFavicon(data?.iconUrl);
    this.createDynamicManifest(data);
    document.title = data?.applicationName || 'RemitAll';
    if (data?.primaryThemeColorHex) {
      const hover = this.adjustColor(data.primaryThemeColorHex, -10);
      const active = this.adjustColor(data.primaryThemeColorHex, -15);
      const border = this.adjustColor(data.primaryThemeColorHex, -20);
      const focusShadow = this.hexToRGB(data.primaryThemeColorHex);
      this.setCSS('--theme-color_1', data.primaryThemeColorHex);
      this.setCSS('--theme-color_1_hover', hover);
      this.setCSS('--theme-color_1_active', active);
      this.setCSS('--theme-color_1_border', border);
      this.setCSS('--theme-color_1_focus-shadow', focusShadow);
    } else {
      this.setCSS('--theme-color_1', '#6e90f6');
      this.setCSS('--theme-color_1_hover', '#5e7ad1');
      this.setCSS('--theme-color_1_active', '#5873c5');
      this.setCSS('--theme-color_1_border', '#536cb9');
      this.setCSS('--theme-color_1_focus-shadow', '132, 161, 247');
    }
    this.setCSS(
      '--theme-color_1_background',
      data?.secondaryThemeColorHex ? data?.secondaryThemeColorHex : '#0a0a0a'
    );
    this.setCSS('--theme-color_2', ' #cccccc');
    let textColor = this.getContrastColor(
      data?.secondaryThemeColorHex ? data?.secondaryThemeColorHex : '#0a0a0a'
    );
    let darkerTextColor = this.darkenColor(textColor, 20);
    this.setCSS('--sidebar-hover-color', textColor);
    this.setCSS('--sidebar-color', darkerTextColor);
  }

  setFavicon(url: string) {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']");

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    if (url) link.href = url;
    else link.href = 'images/favicon.ico';
  }

  setCSS(variable: string, value: string) {
    this.renderer.setStyle(document.documentElement, variable, value);
    document.documentElement.style.setProperty(variable, value);
  }

  adjustColor(hex: string, percent: number): string {
    let [h, s, l] = this.hexToHSL(hex);
    l = Math.max(0, Math.min(100, l + percent));
    return this.hslToHex(h, s, l);
  }

  hexToHSL(hex: string): [number, number, number] {
    let r = parseInt(hex.substring(1, 3), 16) / 255;
    let g = parseInt(hex.substring(3, 5), 16) / 255;
    let b = parseInt(hex.substring(5, 7), 16) / 255;

    let max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  }

  hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    let c = (1 - Math.abs(2 * l - 1)) * s;
    let x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    let m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;

    if (0 <= h && h < 60) {
      r = c;
      g = x;
    } else if (60 <= h && h < 120) {
      r = x;
      g = c;
    } else if (120 <= h && h < 180) {
      g = c;
      b = x;
    } else if (180 <= h && h < 240) {
      g = x;
      b = c;
    } else if (240 <= h && h < 300) {
      r = x;
      b = c;
    } else if (300 <= h && h < 360) {
      r = c;
      b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  }

  hexToRGB(hex: string): string {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  }

  getContrastColor(hex: string): string {
    // Convert HEX to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Calculate brightness (YIQ formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Return black or white based on brightness
    return brightness > 128 ? '#000000' : '#FFFFFF';
  }

  darkenColor(hex: string, percent: number): string {
    // Convert HEX to RGB
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);

    // Darken each color component
    r = Math.max(0, r - (r * percent) / 100);
    g = Math.max(0, g - (g * percent) / 100);
    b = Math.max(0, b - (b * percent) / 100);

    // Convert back to HEX
    return `#${this.toHex(r)}${this.toHex(g)}${this.toHex(b)}`;
  }

  private toHex(value: number): string {
    return value.toString(16).padStart(2, '0');
  }

  async createDynamicManifest(themeObj: any) {
    try {
      let filePath = '';
      let applicationName = '';
      let themeColor = '';
      let secondaryColor = '';
      if (themeObj) {
        // console.log(themeObj);
        filePath = themeObj?.iconUrl
          ? themeObj?.iconUrl
          : `${this.currentOrigin}/assets/images/logo.png`;
        applicationName = themeObj?.applicationName
          ? themeObj?.applicationName
          : 'RemitAll';
        themeColor = themeObj?.primaryThemeColorHex
          ? themeObj?.primaryThemeColorHex
          : '#6e90f6';
        secondaryColor = themeObj?.secondaryThemeColorHex
          ? themeObj?.secondaryThemeColorHex
          : '#ffffff';
      } else {
        // console.log(themeObj);
        filePath = `${this.currentOrigin}/assets/images/logo.png`;
        applicationName = 'RemitAll';
        themeColor = '#6e90f6';
        secondaryColor = '#ffffff';
      }
      // console.log(filePath);
      const resizedIconUrlTo192 = await this.resizeImageTo192(filePath);
      const resizedIconUrlTo512 = await this.resizeImageTo512(filePath);
      const dynamicManifest = {
        name: applicationName,
        short_name: applicationName,
        start_url: `${this.currentOrigin}`,
        display: 'standalone',
        theme_color: themeColor,
        background_color: secondaryColor,
        description:
          'My short project description giving a slightly better idea about my app',
        icons: [
          {
            src: resizedIconUrlTo192,
            sizes: '192x192',
            type: 'image/png',
            purpose: 'any',
          },
          {
            src: resizedIconUrlTo512,
            sizes: '512x512',
            type: 'image/png',
            purpose: 'any',
          },
        ],
      };
      // console.log(dynamicManifest);
      this.addManifestLink(dynamicManifest);
    } catch (err) {
      console.error('Error creating manifest icon:', err);
    }
  }

  resizeImageTo192(url: string): Promise<string> {
    // console.log(url);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // CORS if needed
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 192;
        canvas.height = 192;

        // Draw the image resized to 192x192
        ctx.drawImage(img, 0, 0, 192, 192);

        // Convert canvas to Blob and then to object URL
        canvas.toBlob((blob) => {
          if (!blob) {
            reject('Canvas is empty');
            return;
          }
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        }, 'image/png');
      };
      img.onerror = () => reject('Failed to load image');
      img.src = url + '?not-from-cache-please';
    });
  }

  resizeImageTo512(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // CORS if needed
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 512;
        canvas.height = 512;

        // Draw the image resized to 512x512
        ctx.drawImage(img, 0, 0, 512, 512);

        // Convert canvas to Blob and then to object URL
        canvas.toBlob((blob) => {
          if (!blob) {
            reject('Canvas is empty');
            return;
          }
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        }, 'image/png');
      };
      img.onerror = () => reject('Failed to load image');
      img.src = url;
    });
  }

  addManifestLink(dynamicManifest: any): void {
    // console.log(dynamicManifest);
    const blob = new Blob([JSON.stringify(dynamicManifest)], {
      type: 'application/json',
    });
    const manifestURL = URL.createObjectURL(blob);
    const existingLink = document.querySelector('link[rel="manifest"]');
    if (existingLink) {
      document.head.removeChild(existingLink);
    }
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = manifestURL;

    document.head.appendChild(link);
  }

  // clearAppCache(): void {
  //   if ('caches' in window) {
  //     caches.keys().then((names) => {
  //       names.forEach((name) => {
  //         caches.delete(name);
  //         console.log(`Cache ${name} deleted`);
  //       });
  //     });
  //   }
  // }

  // async clearAndReplaceIcon(icon192: any, icon512: any): Promise<void> {
  //   // const icon192 = '/icons/icon-192x192.png';
  //   // const icon512 = '/icons/icon-512x512.png';

  //   if ('caches' in window) {
  //     const cacheNames = await caches.keys();
  //     for (const cacheName of cacheNames) {
  //       const cache = await caches.open(cacheName);

  //       // Delete existing icons
  //       await cache.delete(icon192);
  //       await cache.delete(icon512);
  //       console.log('Old icons deleted');

  //       // Add new versioned icons
  //       await cache.addAll([
  //         `${icon192}?v=${Date.now()}`,
  //         `${icon512}?v=${Date.now()}`,
  //       ]);
  //       console.log('New icons added');
  //     }
  //   }
  // }

  // unregisterServiceWorker(): Promise<void> {
  //   return navigator.serviceWorker.getRegistrations().then((registrations) => {
  //     for (const registration of registrations) {
  //       registration.unregister().then(() => {
  //         caches.keys().then((names) => {
  //           for (const name of names) caches.delete(name);
  //         });
  //         console.log('Service Worker unregistered & cache cleared');
  //         window.location.reload();
  //       });
  //     }
  //   });
  // }
}
